import { OpenPanel } from '@openpanel/web'
import type { RootLoaderType } from '#app/root'

// Initialize OpenPanel instance
let opInstance: OpenPanel | null = null

export function initializeOpenPanel(
	user?: Awaited<ReturnType<RootLoaderType>>['data']['user'],
	slug?: string,
) {
	if (typeof window === 'undefined') return
	if (window.ENV.APP_ENV !== 'production') return

	if (!opInstance) {
		opInstance = new OpenPanel({
			clientId: 'c4d5a812-0a05-4cf1-b0d6-8e79f61964c3',
			trackScreenViews: true,
			trackOutgoingLinks: false,
			trackAttributes: false,
		})
	}

	// Identify user if available
	if (user?.id) {
		void opInstance.identify({
			profileId: user.id,
			email: user.email,
			firstName: user.name,
			properties: {
				workspace: slug ?? 'unknown',
			},
		})
	}

	return opInstance
}

export function getOpenPanel(): OpenPanel | null {
	return opInstance
}

// Helper function to track events with consistent naming
export function trackEvent(
	eventName: string,
	properties?: Record<string, any>,
) {
	if (window.ENV.APP_ENV !== 'production') return

	if (!opInstance) {
		console.warn('OpenPanel not initialized')
		return
	}

	void opInstance.track(eventName, properties)
}

// Page view tracking helper (called automatically with trackScreenViews: true)
export function trackPageView(
	pathname: string,
	properties?: Record<string, any>,
) {
	if (!opInstance) return

	void opInstance.track('page_viewed', {
		path: pathname,
		...properties,
	})
}

// Feature-specific tracking functions
export const AnalyticsEvents = {
	// Chat events
	CHAT_OPENED: 'chat_opened',
	CHAT_MESSAGE_SENT: 'chat_message_sent',
	CHAT_CLOSED: 'chat_closed',
	CHAT_NEW_CREATED: 'chat_new_created',
	CHAT_HISTORY_VIEWED: 'chat_history_viewed',
	CHAT_INSIGHT_SELECTED: 'chat_insight_selected',
	CHAT_SHARED: 'chat_shared',
	CHAT_UNSHARED: 'chat_unshared',
	SHARED_CHAT_VIEWED: 'shared_chat_viewed',
	SHARED_CHATS_TAB_VIEWED: 'shared_chats_tab_viewed',
	CHAT_SPEECH_RECORDING_STARTED: 'chat_speech_recording_started',
	CHAT_SPEECH_RECORDING_STOPPED: 'chat_speech_recording_stopped',
	CHAT_SPEECH_RECORDING_CANCELLED: 'chat_speech_recording_cancelled',
	CHAT_SPEECH_TRANSCRIPTION_COMPLETED: 'chat_speech_transcription_completed',
	CHAT_SPEECH_TRANSCRIPTION_FAILED: 'chat_speech_transcription_failed',

	// Memory events
	MEMORY_CREATED: 'memory_created',
	MEMORY_UPDATED: 'memory_updated',
	MEMORY_DELETED: 'memory_deleted',
	MEMORY_VIEWED: 'memory_viewed',

	// Graph config events
	GRAPH_CONFIG_VIEWED: 'graph_config_viewed',
	GRAPH_CONFIG_ENTITY_CREATED: 'graph_config_entity_created',
	GRAPH_CONFIG_ENTITY_UPDATED: 'graph_config_entity_updated',
	GRAPH_CONFIG_RELATIONSHIP_CREATED: 'graph_config_relationship_created',
	GRAPH_CONFIG_PUBLISHED: 'graph_config_published',
	GRAPH_CONFIG_VIEW_CHANGED: 'graph_config_view_changed',

	// Topic events
	TOPIC_CREATED: 'topic_created',
	TOPIC_UPDATED: 'topic_updated',
	TOPIC_DELETED: 'topic_deleted',
	TOPIC_PROCESSING_TRIGGERED: 'topic_processing_triggered',
	TOPIC_ANALYSIS_VIEWED: 'topic_analysis_viewed',
	TOPIC_TEMPLATE_SELECTED: 'topic_template_selected',

	// Task events
	TASK_CREATED: 'task_created',
	TASK_UPDATED: 'task_updated',
	TASK_COMPLETED: 'task_completed',
	TASK_DELETED: 'task_deleted',
	TASK_VIEWED: 'task_viewed',

	// Board events
	BOARD_VIEWED: 'board_viewed',
	BOARD_CREATED: 'board_created',
	BOARD_UPDATED: 'board_updated',
	BOARD_DELETED: 'board_deleted',
	BOARD_SHARED: 'board_shared',
	BOARD_SHARE_UPDATED: 'board_share_updated',
	BOARD_DASHBOARD_VIEWED: 'board_dashboard_viewed',
	BOARD_DASHBOARD_CREATED: 'board_dashboard_created',
	BOARD_INSIGHT_VIEWED: 'board_insight_viewed',
	BOARD_INSIGHT_PRESENTATION_VIEWED: 'board_insight_presentation_viewed',
	BOARD_INSIGHT_SAVED: 'board_insight_saved',
	BOARD_CHART_SAVED: 'board_chart_saved',
	BOARD_COMMENT_SENT: 'board_comment_sent',
	BOARD_ASK_ABOUT_CLICKED: 'board_ask_about_clicked',
	BOARD_EXPORT_INITIATED: 'board_export_initiated',

	// Export events
	CHAT_EXPORT_BUTTON_CLICKED: 'chat_export_button_clicked',
	CHAT_EXPORT_PANEL_OPENED: 'chat_export_panel_opened',
	CHAT_EXPORT_PANEL_CLOSED: 'chat_export_panel_closed',
	CHAT_EXPORT_TAB_SWITCHED: 'chat_export_tab_switched',
	CHAT_EXPORT_COPY_CLICKED: 'chat_export_copy_clicked',
	CHAT_EXPORT_PDF_DOWNLOADED: 'chat_export_pdf_downloaded',
	CHAT_EXPORT_REPORT_GENERATED: 'chat_export_report_generated',
	CHAT_EXPORT_ADVANCED_OPTIONS_TOGGLED: 'chat_export_advanced_options_toggled',
	CHAT_EXPORT_HISTORY_CHANGED: 'chat_export_history_changed',

	// Sources panel events
	CHAT_SOURCES_BUTTON_CLICKED: 'chat_sources_button_clicked',
	CHAT_SOURCES_PANEL_OPENED: 'chat_sources_panel_opened',
	CHAT_SOURCES_PANEL_CLOSED: 'chat_sources_panel_closed',

	// Chat UI events
	CHAT_ANSWERS_EXPANDED_ALL: 'chat_answers_expanded_all',
	CHAT_ANSWERS_COLLAPSED_ALL: 'chat_answers_collapsed_all',
} as const
